interface ToastPayload {
  variant: "default" | "destructive" | "success" | "alert";
  description: string;
  duration: number;
}

const DEFAULT_DURATION: number = 3000;

const PARTIAL_DURATION: number = 8000;

const vouchersLabel = (count: number): string =>
  count === 1 ? "1 vale" : `${count} vales`;

export const PAYROLL_VOUCHER_TOASTS = {
  DECISION_CONFIRMED: (
    employeeName: string,
    approved: boolean,
  ): ToastPayload => ({
    variant: "success",
    description: `${employeeName} — vale ${
      approved ? "aprovado" : "reprovado"
    } e enviado ao Financeiro`,
    duration: DEFAULT_DURATION,
  }),
  DECIDED_ELSEWHERE: (
    employeeName: string,
    decidedBy: string,
    approved: boolean,
  ): ToastPayload => ({
    variant: "default",
    description: `${employeeName} — ${
      approved ? "aprovado" : "reprovado"
    } agora por ${decidedBy}. A linha saiu da sua fila.`,
    duration: 6000,
  }),
  BATCH_SUCCESS: (count: number, approved: boolean): ToastPayload => ({
    variant: "success",
    description: `${vouchersLabel(count)} ${
      approved ? "aprovado(s)" : "reprovado(s)"
    } com sucesso.`,
    duration: DEFAULT_DURATION,
  }),
  BATCH_PARTIAL: (
    count: number,
    skippedSummary: string,
    approved: boolean,
  ): ToastPayload => ({
    variant: "alert",
    description: `${vouchersLabel(count)} ${
      approved ? "aprovado(s)" : "reprovado(s)"
    }. Ficaram de fora: ${skippedSummary}.`,
    duration: PARTIAL_DURATION,
  }),
  BATCH_NOTHING_DONE: (skippedSummary: string): ToastPayload => ({
    variant: "destructive",
    description: `Nenhum vale foi decidido. Motivo: ${skippedSummary}.`,
    duration: PARTIAL_DURATION,
  }),
  EXPORT_REQUESTED: (label: string): ToastPayload => ({
    variant: "default",
    description: `Exportando ${label}`,
    duration: DEFAULT_DURATION,
  }),
  EXPORT_EMPTY: (): ToastPayload => ({
    variant: "destructive",
    description: "Nenhum vale aprovado pendente de exportação neste ciclo",
    duration: DEFAULT_DURATION,
  }),
  DECISION_FAILED: (description: string): ToastPayload => ({
    variant: "destructive",
    description,
    duration: 6000,
  }),
} as const;

export const PAYROLL_VOUCHER_LABELS = {
  // O export deixou de fechar o período (ADR-PAY-006 da API): ele carimba o que
  // leva, e o que entrar depois sai numa exportação seguinte do MESMO ciclo.
  EXPORT_CLOSES_PERIOD_TITLE: "Confirmar exportação da Consolidada",
  EXPORT_CYCLE_TARGET: (cycle: string): string => `Ciclo ${cycle}`,
  EXPORT_CLOSES_PERIOD:
    "As parcelas incluídas neste arquivo serão carimbadas como enviadas ao Sênior e não entrarão em exportações futuras. A partir daqui, o Gerente não consegue mais cancelar sem gerar Perda.",
  EXPORT_IS_INCREMENTAL:
    "Você pode exportar este ciclo quantas vezes precisar: cada arquivo traz apenas o que ainda não foi carimbado. O total do ciclo é a soma dos arquivos.",
  EXPORT_CONFIRM: "Exportar",
  EXPORT_ACKNOWLEDGEMENT:
    "Estou ciente de que esta exportação carimba as parcelas como enviadas ao Sênior, que elas não entrarão em exportações futuras, e que a partir daqui o Gerente não consegue mais cancelar sem gerar Perda.",
  EMPTY_STATE: "Nenhum vale neste filtro",
  RELOAD: "Recarregar",
  RELOADING: "Atualizando...",
  REFRESHED_AT: (moment: Date): string =>
    `Atualizado às ${moment.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })}`,
  DECIDED_BY: (email: string): string => `por ${email}`,
  DIFFERENCE_AT: (date: string): string => `Diferença em ${date}`,
  APPROVE: "Aprovar",
  REJECT: "Reprovar",
  ROW_ACTIONS: "Abrir decisões deste vale",
  ALREADY_DECIDED: "Status já definido",
  SELECT_ALL: "Selecionar todos os vales aguardando",
  SELECT_ROW: "Selecionar este vale",
  SELECT_NONE_AVAILABLE: "Nenhum vale aguardando decisão nesta página",

  DECISION_TITLE_APPROVE: "Confirmar aprovação",
  DECISION_TITLE_REJECT: "Confirmar reprovação",
  DECISION_DESCRIPTION_APPROVE: "Confira a consequência antes de confirmar",
  DECISION_DESCRIPTION_REJECT: "Confira a consequência antes de confirmar",
  CONFIRM_APPROVE: "Sim, aprovar",
  CONFIRM_REJECT: "Sim, reprovar",
  CANCEL: "Cancelar",
  PROCESSING: "Processando...",

  // A consequencia sai do codigo: `pendingInstallments` filtra `status IN (APPROVED)`, entao reprovado nunca entra na planilha.
  CONSEQUENCE_APPROVE: (installments: number, firstAmount: string): string =>
    installments > 1
      ? `O vale entra na Planilha Descontos em Folha e o colaborador será descontado em ${installments}× de ${firstAmount}.`
      : `O vale entra na Planilha Descontos em Folha e o colaborador será descontado em parcela única de ${firstAmount}.`,
  CONSEQUENCE_REJECT:
    "O vale não entra na planilha e o colaborador não será descontado. A decisão é definitiva.",
  NOT_DISCOUNTABLE:
    "Este vale não tem valor a descontar e não aceita decisão. Para desfazê-lo, use a reversão em Gerenciar Perdas.",

  INSTALLMENTS: "Parcelas",
  INSTALLMENTS_PLANNED_BY_MANAGER:
    "Parcelamento definido pelo Gerente. Você aprova ou recusa esta quantidade.",
  COMMENT_PLACEHOLDER: "Comentário (opcional)",
  REJECTION_REASON_LABEL: "Motivo da reprovação",
  REJECTION_REASON_PLACEHOLDER:
    "Explique por que este vale não vai para a folha",
  REJECTION_REASON_HINT: (min: number, max: number): string =>
    `Entre ${min} e ${max} caracteres. Vai para a trilha de auditoria.`,
  REJECTION_REASON_TOO_SHORT: (min: number): string =>
    `Descreva o motivo com pelo menos ${min} caracteres`,

  BATCH_SELECTED: (count: number): string =>
    count === 1 ? "1 vale selecionado" : `${count} vales selecionados`,
  BATCH_APPROVE: "Aprovar selecionados",
  BATCH_REJECT: "Reprovar selecionados",
  BATCH_APPROVE_TITLE: "Confirmar aprovação em lote",
  BATCH_REJECT_TITLE: "Confirmar reprovação em lote",
  BATCH_CONFIRM_TITLE: (action: string): string =>
    `${action} os vales selecionados`,
  BATCH_INTRO: (action: string, count: number): string =>
    `${action} ${vouchersLabel(count)}. Confira as parcelas antes de confirmar.`,
  BATCH_APPROVE_EFFECT: (count: number): string =>
    `${vouchersLabel(count)} entrará(ão) na Planilha Descontos em Folha e será(ão) descontado(s) do salário.`,
  BATCH_REJECT_EFFECT: (count: number): string =>
    `${vouchersLabel(count)} não entrará(ão) na planilha e não será(ão) descontado(s). A decisão é definitiva.`,
  BATCH_WILL_PASS: "Serão processados",
  BATCH_WILL_SKIP: "Serão pulados",
  BATCH_NOTHING_TO_PROCESS:
    "Nenhum dos vales selecionados aceita decisão agora.",
  BATCH_TOTAL: "Total",
  BATCH_NO_SELECTION: "Selecione ao menos um vale para continuar.",

  CONCILIATION_LINK: (conciliationId: number): string =>
    `Conc. #${conciliationId}`,
  CONCILIATION_LINK_TITLE: "ID da conciliação no Financeiro",
  CYCLE_OPEN: "Ciclo aberto",
  DAYS_TO_CLOSE: "Dias para fechar",
  VOUCHERS_IN_CYCLE: "Vales no ciclo",
  PAYROLL_DISCOUNT_IN_CYCLE: "Desconto em folha no ciclo",
  DECIDED_PERCENTAGE: (percentage: number): string =>
    `${percentage}% decididos`,
  EXPORTED_PERCENTAGE: (percentage: number): string =>
    `${percentage}% exportados`,
  DAYS: (days: number): string => `${days} dias`,
  EXPORT_MENU: "Exportar Tabelas",
  EXPORT_RANGE_REQUIRES_PERIOD:
    "Selecione um período para exportar a planilha.",
  PERIOD_PLACEHOLDER: "Selecione um período",
  CLEAR_PERIOD: "Limpar período",
} as const;

export const PAYROLL_VOUCHER_PAGINATION_LABELS = {
  PAGE_OF: (current: number, last: number): string =>
    `Página ${current} de ${last}`,
  RESULT_COUNT: (total: number): string => `${total} vale(s) encontrado(s).`,
} as const;
